import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { StepperModule } from 'primeng/stepper';
import { TableLazyLoadEvent } from 'primeng/table';
import { ReportService } from '../../services/report.service';
import { WebsocketService } from '../../services/websocket.service';
import { DialogBase } from '../../shared/components/dialogs/dialog-base/dialog-base';
import { TableComponent } from '../../shared/components/table/table';
import { TranslateSafePipe } from '../../shared/pipes/translation.pipe';
import { formatTimeAgoTranslation, getTimeRangeStringTranslation } from '../../shared/utils/date.util';
import { LogRequestDto } from '../../types/dtos/log.dto';
import { GetCreatedReportsResponseDto, ReportDto } from '../../types/dtos/reports.dto';
import { WebsocketResponseDto } from '../../types/dtos/websocket.dto';
import { ReportGenerationStep, ReportType } from '../../types/enums/report.enum';
import { DomainEnum } from '../../types/enums/websocket.enum';
import { ReportCreatePreset, ReportRow, WebSocketReportStatus } from '../../types/interfaces/report.interface';
import { TableConfig, TableRowDto } from '../../types/interfaces/table.interface';
import { DashboardBoxComponent } from '../dashboard/dashboard-box/dashboard-box.component';
import { ReportCreate } from './model/report-create/report-create';
import {LogService} from '../../services/log.service';
import {LoggingActionEnum, LoggingTargetEnum} from '../../types/enums/logging.enum';

@Component({
  selector: 'app-reports',
  imports: [
    ReactiveFormsModule,
    StepperModule,
    DashboardBoxComponent,
    TableComponent,
    DialogBase,
    ReportCreate,
    TranslateSafePipe,
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.css',
})
export class ReportsComponent implements AfterViewInit, OnDestroy {
  public reportStatuses: Map<string, WebSocketReportStatus> = new Map();

  public get activeReportStatuses(): WebSocketReportStatus[] {
    return Array.from(this.reportStatuses.values());
  }

  public loading: boolean = false;
  public reportFiles: TableRowDto<ReportRow>[] = [];
  public reportCreatePreset: ReportCreatePreset | null = null;

  public limit: number = 25;
  public offset: number = 0;
  public sortBy: string | null = null;
  public orderBy: 'asc' | 'desc' | null = null;

  public filterRequest: LogRequestDto = {
    limit: this.limit,
    offset: this.offset,
  };

  public isCreateReportDialogOpen: boolean = false;
  public isReportPreviewOpen: boolean = false;
  public reportPreviewUrl: SafeResourceUrl | null = null;
  public reportPreviewTitle: string = 'common.actions.preview-report';
  public reportCreateTitle: string = 'common.actions.create-report';

  public readonly reportFilesTableConfig: TableConfig<ReportRow> = {
    columns: [
      {
        key: 'fileName',
        title: 'common.fields.filename',
        isTranslated: true,
      },
      {
        key: 'format',
        title: 'Format',
        isTag: true,
        overrideValue: (row: TableRowDto<ReportRow>) => this._reportService.mapExportFormatLabel(row.format),
        getTagColor: (row: TableRowDto<ReportRow>) => this._reportService.mapExportFormatColor(row.format),
      },
      {
        key: 'reportType',
        title: 'common.fields.report-type',
        isTranslated: true,
        isValueTranslated: true,
        overrideValue: (row: TableRowDto<ReportRow>) => 'common.enum.report-type.' + row.reportType,
      },
      {
        key: 'dateRange',
        title: 'common.fields.date-range',
        isTranslated: true,
      },
      {
        key: 'fileSize',
        title: 'common.fields.file-size',
        isTranslated: true,
      },
      {
        key: 'createdAt',
        title: 'common.fields.created-at',
        isTranslated: true,
      },
    ],
    mobile: {
      title: {
        key: 'reportType',
        isValueTranslated: true,
        overrideValue: (row: TableRowDto<ReportRow>) => 'common.enum.report-type.' + row.reportType,
        tag: 'format',
        overrideTag: (row: TableRowDto<ReportRow>) => this._reportService.mapExportFormatLabel(row.format),
        getTagColor: (row: TableRowDto<ReportRow>) => this._reportService.mapExportFormatColor(row.format),
      },
      description: {
        key: 'fileName',
      },
      sections: [
        {
          key: 'dateRange',
          isTranslated: true,
          title: 'common.fields.date-range',
        },
        {
          key: 'fileSize',
          isTranslated: true,
          title: 'common.fields.file-size',
        },
        {
          key: 'createdAt',
          isValueTranslated: true,
          isTranslated: true,
          title: 'common.fields.created-at',
        },
      ],
    },
    pagination: true,
    showRows: 10,
    rowsPerPageOptions: [
      10,
      25,
      50,
    ],
    emptyMessage: 'common.messages.empty.data',
    actions: [
      {
        icon: 'visibility',
        action: (row: TableRowDto<ReportRow>) => {
          this.downloadReport(row.fileName);
        },
      },
    ],
    lazy: true,
    onLazyLoad: (event: TableLazyLoadEvent) => this.loadReports(event),
  };

  constructor(
    private readonly _reportService: ReportService,
    private readonly _domSanitizer: DomSanitizer,
    private readonly _translateService: TranslateService,
    private readonly _route: ActivatedRoute,
    private readonly _websocketService: WebsocketService,
    private readonly _loggingService: LogService,
  ) {
  }

  ngOnDestroy(): void {
    this._websocketService.removeSubscriptions(DomainEnum.REPORT, 'create');
  }

  ngAfterViewInit(): void {
    this._route.queryParams.subscribe((params: Params) => {
      let type: ReportType | undefined = params['type'];
      let device_eui: string | undefined = params['device_eui'];
      let from: number | undefined = params['from'];
      let to: number | undefined = params['to'];

      if (!type) return;

      this.reportCreatePreset = {
        type,
        device_eui,
        from,
        to,
      };

      this.openCreateDialog();
    });

    this._websocketService.subscribe(DomainEnum.REPORT, 'create', (data: WebsocketResponseDto) => {
      let params: WebSocketReportStatus = data.params as WebSocketReportStatus;
      if (!params?.id) return;

      this.reportStatuses.set(params.id, params);
      if (params.step === ReportGenerationStep.COMPLETED) {
        if (params.fileName) {
          this.openReportPreview(params.fileName);
          this.loadReports({});
        }

        setTimeout(() => {
          this.reportStatuses.delete(params.id);
        }, 2000);
      } else if (params.step === ReportGenerationStep.FAILED) {
        setTimeout(() => {
          this.reportStatuses.delete(params.id);
        }, 30000);
      }
    });
  }

  public loadReports(event: TableLazyLoadEvent): void {
    this.limit = event.rows ?? this.limit;
    this.offset = event.first ?? 0;
    const sortField: string | string[] | null | undefined = event.sortField;
    if (sortField && !Array.isArray(sortField)) {
      this.sortBy = sortField ?? null;
      this.orderBy = event.sortOrder === 1 ? 'asc' : 'desc';
    } else {
      this.sortBy = null;
      this.orderBy = null;
    }
    this._updateData();
  }

  public openCreateDialog(): void {
    this.isCreateReportDialogOpen = true;
  }

  public readonly closeCreateDialog = (): void => {
    this.isCreateReportDialogOpen = false;
  };

  public readonly closeReportPreviewDialog = (): void => {
    this.isReportPreviewOpen = false;
    this.reportPreviewUrl = null;
  };


  public downloadReport(fileName: string): void {
    this._loggingService.sendLog({
      target: LoggingTargetEnum.REPORT,
      action: LoggingActionEnum.VIEW,
      description: fileName
    });
    window.open(this._reportService.getReportDownloadUrl(fileName), '_blank');
  }

  private _setFilter(): void {
    this.filterRequest = {
      limit: this.limit,
      offset: this.offset,
    };

    if (this.sortBy) {
      this.filterRequest.sortBy = this.sortBy;
    }
    if (this.orderBy) {
      this.filterRequest.orderBy = this.orderBy;
    }
  }

  private _updateData(): void {
    this.loading = true;

    this._setFilter();

    this._reportService.getCreatedReports(this.filterRequest)
      .subscribe({
        next: (response: GetCreatedReportsResponseDto) => {
          this.reportFiles = this.mapReportFiles(response.data ?? []);
          this.reportFilesTableConfig.totalRecords = response.rowCount;
          this.loading = false;
        },
        error: (error: unknown) => {
          console.error('Error loading created reports:', error);
          this.reportFiles = [];
          this.loading = false;
        },
      });
  }

  private mapReportFiles(
    reports: ReportDto[],
  ): TableRowDto<ReportRow>[] {
    return reports.map((report: ReportDto) => {
      let params: Record<string, unknown> = JSON.parse(report.params);

      return {
        id: report.id,
        fileName: report.fileName,
        reportType: report.type,
        format: report.format,
        fileSize: this.formatFileSize(report.fileSize),
        dateRange: getTimeRangeStringTranslation(
          this._translateService,
          params['fromDate'] as number,
          params['toDate'] as number,
        ),
        createdAt: formatTimeAgoTranslation(this._translateService, report.created_at),
      };
    });
  }

  private formatFileSize(fileSize: number): string {
    return `${ this.roundMegabytes(fileSize / 1024 / 1024) } MB`;
  }

  private roundMegabytes(value: number): string {
    return value.toFixed(2)
      .replace(/\.00$/, '');
  }

  private openReportPreview(fileName: string): void {
    const reportUrl: string = this._reportService.getReportDownloadUrl(fileName);

    this.reportPreviewTitle = `${ this._translateService.instant('common.title.report-preview') }: ${ fileName }`;
    this.reportPreviewUrl = this._domSanitizer.bypassSecurityTrustResourceUrl(reportUrl);
    this.isReportPreviewOpen = true;
  }
}
